<script lang="ts">
	import { DashControlWidget } from '@Shelly/shelly-design-system/index';
	import { status_configurator } from 'src/helpers/device_data_helpers';
	import { real_id } from 'src/helpers/interface_helpers';
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
import { devices } from 'src/stores/lists/devices';
	import { getDeviceState, defaultStateType } from 'src/stores/device-runtime/states';
	import { edit } from 'src/stores/ui/edit_lists';
	import { sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	import { beforeUpdate } from 'svelte';
	import DashDeviceControls from './components/DashDeviceControls.svelte';
	import {
		computeLocalTimer,
		getAdditionalText,
		getAvatar,
	} from 'src/components/Dashboards/dash-helpers';
	import { getDeviceTimerState, type DeviceTimerState } from 'src/helpers/device_control_on_off';
	import {
		getCriticalNotifications,
		getNotificationsForDevice,
	} from 'src/helpers/notifications/device_notifications_helper';

	export let deviceWidget;

	let statuses, description, isLoading, icon;
	let notAuthenticated;
	let timerState: DeviceTimerState = { hasTimer: false, timer: null, timerStartedAt: undefined };
	let tickInterval: ReturnType<typeof setInterval> | null = null;
	let cachedTimerStartedAt: number | undefined;
	let cachedDuration = 0;

	function startTimerTick() {
		stopTimerTick();
		tickInterval = setInterval(() => {
			const { time, percentage } = computeLocalTimer(cachedDuration, cachedTimerStartedAt);

			timerState = {
				hasTimer: time > 0,
				timer: { time, percentage },
				timerStartedAt: cachedTimerStartedAt,
			};
			if (time <= 0) stopTimerTick();
		}, 50);
	}

	function stopTimerTick() {
		if (tickInterval) {
			clearInterval(tickInterval);
			tickInterval = null;
		}
	}

	$: deviceStateStore = getDeviceState(device?.id);
	$: deviceState = $deviceStateStore;
	$: device = $devices[deviceWidget?.id];
	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;
	$: isUpdating = deviceState?.type === 3;
	$: isDefaultState = deviceState?.type == defaultStateType;
	$: value = !device ? deviceWidget?.id : device?.name;
	$: avatar = getAvatar(device);
	$: notificationsToShow = getNotificationsForDevice(device?.id);
	$: criticalNotifications = getCriticalNotifications(notificationsToShow);
	$: additionalText = getAdditionalText(
		device,
		isUpdating,
		timerState?.hasTimer,
		timerState?.timer?.time,
		deviceState,
		criticalNotifications,
	);

	$: storeTimerState =
		device && status && settings
			? getDeviceTimerState(device, status, settings)
			: { hasTimer: false, timer: null, timerStartedAt: undefined };

	$: {
		if (storeTimerState.hasTimer && !isLoading && isDefaultState) {
			if (storeTimerState.timerStartedAt !== cachedTimerStartedAt) {
				cachedTimerStartedAt = storeTimerState.timerStartedAt;
				cachedDuration = storeTimerState.timer.duration;
				startTimerTick();
			} else if (!tickInterval) {
				startTimerTick();
			}
		} else {
			if (tickInterval) stopTimerTick();
			if (!isLoading && isDefaultState) {
				timerState = storeTimerState;
			} else {
				timerState = { hasTimer: false, timer: null, timerStartedAt: undefined };
				cachedTimerStartedAt = undefined;
			}
		}
	}

	beforeUpdate(() => {
		if (device && Object.keys(device).length && Object.keys(deviceState).length) {
			statuses = status_configurator(status, device, settings);
			isLoading = deviceState?.isLoading;
			icon = deviceState?.icon;
			description = deviceState?.description;
			notAuthenticated = deviceState?.type === 9;
		}
	});
</script>

<DashControlWidget
	aria-label="Dash Control Widget"
	class="cursor-pointer"
	{avatar}
	{value}
	{additionalText}
	selected={deviceWidget.id === $sidePanelStateCombined?.id}
	offline={!device ? false : !status?.online}
	hideSlotRight={!device || (device?.mainActionType === 'none' && isDefaultState) || $edit}
	on:click
>
	<DashDeviceControls
		slot="right"
		type={''}
		useV2Components
		{device}
		{status}
		{settings}
		{statuses}
		{isLoading}
		{notAuthenticated}
		labelV2Props={{
			size: 's',
			isVertical: true,
		}}
		devBtnsV2Props={{
			deviceButtonV2: { size: 's' },
			doubleDeviceButtonV2: { size: 's' },
			tripleDeviceButtonV2: { size: 's' },
		}}
	/>
</DashControlWidget>
